import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { SearchField, Sheet } from '../ui';
import { cx } from '../ui/cx';
import { searchDestinations } from './navigation';
import { iconFor } from './icons';
import styles from './GlobalSearch.module.css';

/**
 * Globale Suche (Lupe oben, Strg+K): springt zu jeder erlaubten Seite
 * oder startet die Gesamtsuche (Teile, Doku, News, Easyhelp, Archiv) mit dem eingegebenen Begriff.
 */
export default function GlobalSearch({ open, onClose, navigation }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (open) { setQuery(''); setActive(0); }
  }, [open]);

  const results = useMemo(() => {
    const pages = searchDestinations(navigation, query).slice(0, 8)
      .map((item) => ({ key: item.path, label: item.label, hint: item.area, icon: iconFor(item.icon), path: item.path }));
    const term = query.trim();
    return term
      ? [{ key: '__all', label: `Überall suchen nach „${term}“`, hint: 'Teile, Doku, News …', icon: Search, path: `/search?search=${encodeURIComponent(term)}` }, ...pages]
      : pages;
  }, [navigation, query]);

  const go = (result) => {
    onClose();
    navigate(result.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); go(results[active]); }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Suchen" size="md">
      <div className={styles.wrap} onKeyDown={onKeyDown}>
        <SearchField
          value={query}
          onChange={(value) => { setQuery(value); setActive(0); }}
          placeholder="Seite oder Begriff, z. B. Kunden, Drucker …"
          autoFocus
          aria-controls="global-search-results"
          aria-activedescendant={results[active] ? `gs-${active}` : undefined}
        />
        {results.length === 0 ? (
          <p className={styles.empty}>Keine passende Seite gefunden.</p>
        ) : (
          <ul id="global-search-results" role="listbox" aria-label="Ergebnisse" className={styles.list}>
            {results.map((result, index) => {
              const Icon = result.icon;
              return (
                <li
                  key={result.key}
                  id={`gs-${index}`}
                  role="option"
                  aria-selected={index === active}
                  className={cx(styles.item, index === active && styles.active)}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(result)}
                >
                  <span className={styles.icon}><Icon aria-hidden /></span>
                  <span className={styles.text}>
                    <span className={styles.label}>{result.label}</span>
                    {result.hint && <span className={styles.hint}>{result.hint}</span>}
                  </span>
                  <ArrowRight className={styles.arrow} aria-hidden />
                </li>
              );
            })}
          </ul>
        )}
        <p className={styles.shortcut}>Tipp: Am PC öffnest du die Suche jederzeit mit <kbd>Strg</kbd> + <kbd>K</kbd>.</p>
      </div>
    </Sheet>
  );
}
